import React, { useEffect, useRef, useState } from "react";
import { CommonFlex, CommonGrid } from "styledComponents/common";
import { formatDelta, getDeltaType } from "utils/helper";

import { currencySymbolMap } from "../../../utils/currencies";
import { DashBoardCommonGrid } from "../../DashBoardNew/dashBoardNewStyles";
import RevenueCompareChart from "./RevenueCompareChart";
import SmallInfoCard from "./SmallInfoCard";

const DashBoardThirdSection = ({
  getDashboardRevenue,
  revenueIsLoading,
  lastYearRevenue,
  thisYearRevenue,
  currentMonthIndex,
  selectedOrganization,
  getDashboardOrganizationSales,
  organizationSalesIsLoading,
  organizationSalesData,
}) => {
  const [cardsData, setCardsData] = useState([]);
  const [maxCardHeight, setMaxCardHeight] = useState(0);
  const cardRefs = useRef([]);
  const currency = localStorage.getItem("currency") || "USD";
  const currencySymbol = currencySymbolMap[currency] || "$";

  // Format currency
  const formatCurrency = value => {
    if (value === null || value === undefined) {
      return `${currencySymbol}0`;
    }

    try {
      // Try to use Intl.NumberFormat with the currency code
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
      return formatted;
    } catch (error) {
      // Fallback: manually format with symbol if Intl doesn't support the currency
      const formattedValue = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
      return `${currencySymbol}${formattedValue}`;
    }
  };

  // Format percentage
  const formatPercentage = formatDelta;

  // Determine delta type based on percentage change
  const getDeltaTypeFn = getDeltaType;

  // Transform API data to card format
  const transformSalesData = apiData => {
    // Always return 6 cards with default values of 0 if data is missing
    const cards = [];

    // Sales Year
    cards.push({
      title: "Sales This Year",
      amount:
        apiData?.sales_year?.this_year !== undefined &&
        apiData?.sales_year?.this_year !== null
          ? formatCurrency(apiData.sales_year.this_year)
          : formatCurrency(0),
      delta:
        apiData?.sales_year?.percentage_change !== undefined &&
        apiData?.sales_year?.percentage_change !== null
          ? formatPercentage(apiData.sales_year.percentage_change)
          : null,
      deltaType:
        apiData?.sales_year?.percentage_change !== undefined &&
        apiData?.sales_year?.percentage_change !== null
          ? getDeltaType(apiData.sales_year.percentage_change)
          : null,
      subText: "vs last year",
    });

    // Sales Month
    cards.push({
      title: "Sales This Month",
      amount:
        apiData?.sales_month?.this_month !== undefined &&
        apiData?.sales_month?.this_month !== null
          ? formatCurrency(apiData.sales_month.this_month)
          : formatCurrency(0),
      delta:
        apiData?.sales_month?.percentage_change !== undefined &&
        apiData?.sales_month?.percentage_change !== null
          ? formatPercentage(apiData.sales_month.percentage_change)
          : null,
      deltaType:
        apiData?.sales_month?.percentage_change !== undefined &&
        apiData?.sales_month?.percentage_change !== null
          ? getDeltaType(apiData.sales_month.percentage_change)
          : null,
      subText: "vs last month",
    });

    // Average Sales Month
    cards.push({
      title: "Average Sales Per Month",
      amount:
        apiData?.average_sales_month?.this_year !== undefined &&
        apiData?.average_sales_month?.this_year !== null
          ? formatCurrency(apiData.average_sales_month.this_year)
          : formatCurrency(0),
      delta:
        apiData?.average_sales_month?.percentage_change !== undefined &&
        apiData?.average_sales_month?.percentage_change !== null
          ? formatPercentage(apiData.average_sales_month.percentage_change)
          : null,
      deltaType:
        apiData?.average_sales_month?.percentage_change !== undefined &&
        apiData?.average_sales_month?.percentage_change !== null
          ? getDeltaType(apiData.average_sales_month.percentage_change)
          : null,
      subText: "this year",
    });

    // Average Sales Job
    cards.push({
      title: "Average Sales Per Job",
      amount:
        apiData?.average_sales_job?.this_month !== undefined &&
        apiData?.average_sales_job?.this_month !== null
          ? formatCurrency(apiData.average_sales_job.this_month)
          : formatCurrency(0),
      delta:
        apiData?.average_sales_job?.percentage_change !== undefined &&
        apiData?.average_sales_job?.percentage_change !== null
          ? formatPercentage(apiData.average_sales_job.percentage_change)
          : null,
      deltaType:
        apiData?.average_sales_job?.percentage_change !== undefined &&
        apiData?.average_sales_job?.percentage_change !== null
          ? getDeltaType(apiData.average_sales_job.percentage_change)
          : null,
      subText: "this month",
    });

    // Missed Sales
    cards.push({
      title: "Missed Sales",
      amount:
        apiData?.missed_sales?.this_month !== undefined &&
        apiData?.missed_sales?.this_month !== null
          ? formatCurrency(apiData.missed_sales.this_month)
          : formatCurrency(0),
      delta:
        apiData?.missed_sales?.percentage_change !== undefined &&
        apiData?.missed_sales?.percentage_change !== null
          ? formatPercentage(apiData.missed_sales.percentage_change)
          : null,
      deltaType:
        apiData?.missed_sales?.percentage_change !== undefined &&
        apiData?.missed_sales?.percentage_change !== null
          ? getDeltaType(apiData.missed_sales.percentage_change)
          : null,
      subText: "this month",
    });

    // Average Missed Sales
    cards.push({
      title: "Average Missed Sales",
      amount:
        apiData?.average_missed_sales?.this_year !== undefined &&
        apiData?.average_missed_sales?.this_year !== null
          ? formatCurrency(apiData.average_missed_sales.this_year)
          : formatCurrency(0),
      delta:
        apiData?.average_missed_sales?.percentage_change !== undefined &&
        apiData?.average_missed_sales?.percentage_change !== null
          ? formatPercentage(apiData.average_missed_sales.percentage_change)
          : null,
      deltaType:
        apiData?.average_missed_sales?.percentage_change !== undefined &&
        apiData?.average_missed_sales?.percentage_change !== null
          ? getDeltaType(apiData.average_missed_sales.percentage_change)
          : null,
      subText: "this year",
    });

    return cards;
  };

  // Call API when organization changes
  useEffect(() => {
    if (selectedOrganization?.id && getDashboardOrganizationSales) {
      const payload = {
        organization_id: selectedOrganization.id,
      };
      getDashboardOrganizationSales(payload);
    }
  }, [selectedOrganization, getDashboardOrganizationSales]);

  // Update cards data when API data changes
  useEffect(() => {
    // Always show 6 cards with default values of 0 if no data
    const transformedData = transformSalesData(organizationSalesData || {});
    setCardsData(transformedData);
  }, [organizationSalesData]);

  useEffect(() => {
    const measureCards = () => {
      const heights = cardRefs.current
        .map(card => card?.getBoundingClientRect().height || 0)
        .filter(Boolean);

      if (!heights.length) {
        return;
      }

      const tallestHeight = Math.ceil(Math.max(...heights));
      setMaxCardHeight(previousHeight =>
        previousHeight === tallestHeight ? previousHeight : tallestHeight,
      );
    };

    measureCards();

    const resizeObserver = new ResizeObserver(() => {
      measureCards();
    });

    cardRefs.current.forEach(card => {
      if (card) {
        resizeObserver.observe(card);
      }
    });

    window.addEventListener("resize", measureCards);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measureCards);
    };
  }, [cardsData, organizationSalesIsLoading]);

  const registerCardRef = index => element => {
    cardRefs.current[index] = element;
  };

  return (
    <DashBoardCommonGrid
      responsiveBreakpoint="1470px"
      responsiveColumns="1fr"
      columns="1fr 1fr"
      gap="20px"
    >
      <CommonGrid columns="1fr" gap="20px">
        {/* Revenue compare chart Section */}
        <RevenueCompareChart
          getDashboardRevenue={getDashboardRevenue}
          revenueIsLoading={revenueIsLoading}
          selectedOrganization={selectedOrganization}
          lastYearRevenue={lastYearRevenue}
          thisYearRevenue={thisYearRevenue}
          currentMonthIndex={currentMonthIndex}
        />
      </CommonGrid>

      <CommonGrid columns="1fr 1fr" gap="20px">
        {/* left column: 3 small cards */}
        <CommonGrid columns="1fr" gap="12px">
          {cardsData.slice(0, 3).map((item, index) => (
            <SmallInfoCard
              key={index}
              ref={registerCardRef(index)}
              title={item.title}
              amount={item.amount}
              delta={item.delta}
              deltaType={item.deltaType}
              subText={item.subText}
              isLoading={organizationSalesIsLoading}
              cardHeight={maxCardHeight ? `${maxCardHeight}px` : undefined}
            />
          ))}
        </CommonGrid>

        {/* right column: 3 small cards */}
        <CommonGrid columns="1fr" gap="12px">
          {cardsData.slice(3, 6).map((item, index) => (
            <SmallInfoCard
              key={index + 3}
              ref={registerCardRef(index + 3)}
              title={item.title}
              amount={item.amount}
              delta={item.delta}
              deltaType={item.deltaType}
              subText={item.subText}
              isLoading={organizationSalesIsLoading}
              cardHeight={maxCardHeight ? `${maxCardHeight}px` : undefined}
            />
          ))}
        </CommonGrid>
      </CommonGrid>
    </DashBoardCommonGrid>
  );
};

export default DashBoardThirdSection;
